import type {
  RepaymentFrequency,
  RepaymentStructure,
  SimulationParams,
} from "./model";

/**
 * Neutral, round illustrative values the visitor is meant to change. They are
 * not derived from Wayflyer merchant data and do not represent an offer.
 *
 * Both structures are tuned so the resting fee per month reads above 1%
 * (Term Loan 10% over 9 months; Cash Advance 10% at a 17% share of 70k
 * monthly revenue growing 5% a month), rather than anchoring the visitor
 * below it.
 */
export const PRICING_DEFAULTS = {
  amount: 100_000,
  feePercent: 10,
  structure: "term-loan" as RepaymentStructure,
  frequency: "daily" as RepaymentFrequency,
  termMonths: 9,
  revenueSharePercent: 17,
  monthlyRevenue: 70_000,
  // A modest month-on-month rise, rather than a flat forecast: a business
  // taking revenue-share financing is normally growing, and 0% understated
  // how quickly a Cash Advance actually clears.
  monthlyGrowthPercent: 5,
  capEnabled: false,
} as const;

/**
 * Suggested monthly revenue for a funding amount when the visitor has not
 * typed one: offers typically run one to three times monthly revenue (see the
 * homepage eligibility multipliers), so seven tenths of the amount keeps the
 * forecast plausible as the amount slider moves. 100k → 70k.
 */
export function suggestedMonthlyRevenue(amount: number): number {
  return roundToNice(amount * 0.7);
}

export const PRICING_RANGES = {
  amount: { min: 10_000, max: 20_000_000 },
  /**
   * Slider travel; the typed field accepts up to `typedMax`.
   *
   * The 5% floor is the lowest fee worth presenting — below it the figure
   * stops resembling anything underwriting would offer, so the floor is a
   * guard against a misleading scenario rather than a bound anyone reaches.
   */
  feePercent: { min: 5, max: 25, typedMax: 100, step: 0.5 },
  termMonths: { min: 3, max: 18, step: 1 },
  /** Slider travel; the typed field accepts 1 to 100. */
  revenueSharePercent: {
    min: 10,
    max: 30,
    typedMin: 1,
    typedMax: 100,
    step: 1,
  },
  monthlyRevenue: { min: 0, max: 999_999_999 },
  /** Growth only: a shrinking forecast is not a scenario this page models. */
  monthlyGrowthPercent: { min: 0, max: 50, step: 0.5 },
  monthlyCap: { min: 0, max: 999_999_999 },
  revenueOverride: { min: 0, max: 999_999_999 },
} as const;

/** Number of funding-months a visitor can override individually. */
export const OVERRIDE_MONTHS = 12;

/**
 * Suggested monthly cap when the visitor has not set one: roughly what a
 * nine-month Term Loan would collect per month, rounded to a clean figure, so
 * the two structures feel comparable at rest.
 */
export function suggestedMonthlyCap(
  amount: number,
  feePercent: number
): number {
  const total = amount * (1 + feePercent / 100);
  const raw = total / 9;
  return roundToNice(raw);
}

/** Rounds to a "clean" number with two or three significant figures. */
export function roundToNice(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const unit = value / magnitude < 2 ? magnitude / 10 : magnitude / 2;
  return Math.round(value / unit) * unit;
}

/** Slider resolution for the logarithmic amount control. */
export const AMOUNT_SLIDER_STEPS = 1000;

const AMOUNT_LOG_RATIO = Math.log(
  PRICING_RANGES.amount.max / PRICING_RANGES.amount.min
);

/** Maps slider travel (0..steps) to an amount on a log scale, snapped. */
export function amountFromSlider(position: number): number {
  const t = Math.min(1, Math.max(0, position / AMOUNT_SLIDER_STEPS));
  const raw = PRICING_RANGES.amount.min * Math.exp(AMOUNT_LOG_RATIO * t);
  return snapAmount(raw);
}

/** Maps an amount back to slider travel on the same log scale. */
export function sliderFromAmount(amount: number): number {
  const clamped = Math.min(
    PRICING_RANGES.amount.max,
    Math.max(PRICING_RANGES.amount.min, amount)
  );
  return Math.round(
    (Math.log(clamped / PRICING_RANGES.amount.min) / AMOUNT_LOG_RATIO) *
      AMOUNT_SLIDER_STEPS
  );
}

/** Snaps an amount to the increment a person would type at that scale. */
export function snapAmount(value: number): number {
  const increment =
    value < 50_000
      ? 1_000
      : value < 250_000
        ? 5_000
        : value < 1_000_000
          ? 10_000
          : value < 5_000_000
            ? 50_000
            : 100_000;
  const snapped = Math.round(value / increment) * increment;
  return Math.min(
    PRICING_RANGES.amount.max,
    Math.max(PRICING_RANGES.amount.min, snapped)
  );
}

export function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

/** Today's date in the visitor's timezone as an ISO `YYYY-MM-DD` string. */
export function todayIso(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Full simulation params from the defaults for a given funding date. */
export function defaultSimulationParams(startDate: string): SimulationParams {
  return {
    amount: PRICING_DEFAULTS.amount,
    feePercent: PRICING_DEFAULTS.feePercent,
    startDate,
    structure: PRICING_DEFAULTS.structure,
    frequency: PRICING_DEFAULTS.frequency,
    termMonths: PRICING_DEFAULTS.termMonths,
    revenueSharePercent: PRICING_DEFAULTS.revenueSharePercent,
    monthlyRevenue: PRICING_DEFAULTS.monthlyRevenue,
    monthlyGrowthPercent: PRICING_DEFAULTS.monthlyGrowthPercent,
    revenueOverrides: {},
    capEnabled: PRICING_DEFAULTS.capEnabled,
    monthlyCap: suggestedMonthlyCap(
      PRICING_DEFAULTS.amount,
      PRICING_DEFAULTS.feePercent
    ),
  };
}
